import React, {useId} from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Box, Link, useBreakpointValue} from '@chakra-ui/react'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {useUrlFromLink} from '../../helpers/urlHelper'
import ProductDyptichTile from '../../../components/product-dyptich-tile/index.jsx'
import PDCImage from '../../../components/pdc-image/index.jsx'

/**
 * This component is used to display a product with a dyptich image.
 * @param {string} product - The product id.
 * @param {string} subTitle - The subtitle of the product.
 * @param {string} desc - The description of the product.
 * @param {string} linkLabel - The label of the link.
 * @param {object} smallImage - The small image.
 * @param {object} mediumImage - The medium image.
 * @param {object} largeImage - The large image.
 * @param {object} xlargeImage - The xlarge image.
 * @param {object} xxlargeImage - The xxlarge image.
 * @param {string} imageTitle - The title of the image.
 * @param {string} imageAlt - The alt of the image.
 * @param {string} imageLink - The link of the image.
 * @param {string} textColor - The color of the text.
 * @param {string} backgroundColor - The color of the background.
 * @param {string} imagePlacement - The placement of the image.
 * @param {boolean} displayPriority - Should image be loaded with high priority.
 * @returns {React.ReactElement} - The product with a dyptich image.
 */
export const PDCHomeProductDyptichImage = ({
    product,
    subTitle,
    desc,
    linkLabel,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    imageLink,
    textColor,
    backgroundColor,
    imagePlacement,
    displayPriority,
    custom
}) => {
    // Choose image based on breakpoint. If not set, we take the closest image from the previous breakpoints
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumImage,
            largeEl: custom?.largeImage?.disBaseLink || largeImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    const updatedUrl = useUrlFromLink(custom?.imageLink) || imageLink
    const isLinkExternal = custom?.imageLink?.type === 'EXTERNAL'

    const titleId = useId()

    const imgBlock = (
        <Link
            as={isLinkExternal ? Link : RouteLink}
            href={updatedUrl}
            to={updatedUrl}
            width={['100%', '100%', '50%']}
            position="relative"
            _focusVisible={{boxShadow: 'outline'}}
        >
            <PDCImage
                currentImage={currentImage}
                imageWidths={DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice}
                imageAlt={imageAlt}
                imageTitle={imageTitle}
                displayPriority={displayPriority}
                title={subTitle}
                skeletonProps={{aspectRatio: '4 / 5', width: '100%'}}
            />
        </Link>
    )

    return (
        <Box
            as="section"
            aria-describedby={titleId}
            display="flex"
            flexDirection={['column', 'column', 'row']}
            justifyContent="space-between"
            alignItems="stretch"
            width="100%"
            height={'auto'}
            backgroundColor={backgroundColor}
            color={textColor}
        >
            {imagePlacement === 'left (top)' ? (
                <>
                    {imgBlock}
                    <ProductDyptichTile
                        product={product}
                        subTitle={subTitle}
                        desc={desc}
                        linkLabel={linkLabel}
                        titleId={titleId}
                        textColor={textColor}
                        displayPriority={displayPriority}
                    />
                </>
            ) : (
                <>
                    <ProductDyptichTile
                        product={product}
                        subTitle={subTitle}
                        desc={desc}
                        linkLabel={linkLabel}
                        titleId={titleId}
                        textColor={textColor}
                        displayPriority={displayPriority}
                    />
                    {imgBlock}
                </>
            )}
        </Box>
    )
}

PDCHomeProductDyptichImage.propTypes = {
    product: PropTypes.string.isRequired,
    subTitle: PropTypes.string,
    desc: PropTypes.string,
    linkLabel: PropTypes.string.isRequired,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    textColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    imageLink: PropTypes.string.isRequired,
    imagePlacement: PropTypes.string.isRequired,
    displayPriority: PropTypes.bool,
    custom: PropTypes.object
}
